import type { DietaryConcernTag } from '../../shared/menu';
import type { OnboardingChoice } from './store';

// A coaching hint surfaced once per (cuisine, identity) combo per device.
// Drives a dismissible card at the top of the menu screen warning the user
// about hidden ingredients common to the cuisine they're ordering from.
//
// Detection is post-parse — we use the menu's detectedSourceLanguage rather
// than the user's geo so a Thai menu in Berlin still gets the fish-sauce
// warning. Only fires if the user's onboardingChoice indicates the warning
// would be relevant (e.g. fish sauce is irrelevant to a celiac).
export interface CuisineHint {
  id: string;
  title: string;
  body: string;
  // Suggested strict tag the user would add per dish to surface the
  // medically-framed phrasebook ask on the hand-off screen.
  suggestedTag?: DietaryConcernTag;
  suggestedTagLabel?: string;
}

const SE_ASIAN_LANGS = ['th', 'vi', 'km', 'lo', 'id', 'ms', 'tl'];

const HEADS_UP_CHOICES: OnboardingChoice[] = ['halal', 'kosher', 'non_medical'];

export function cuisineHintFor(
  sourceLanguage: string,
  onboardingChoice: OnboardingChoice | null,
): CuisineHint | null {
  // Some users don't need this layer — celiacs care about gluten not fish
  // sauce; medical-allergy users handle their own list. Religious/ethical
  // users are the audience for hidden-animal-product warnings.
  if (!onboardingChoice || !HEADS_UP_CHOICES.includes(onboardingChoice)) {
    return null;
  }

  // Southeast Asian cuisines — fish sauce, shrimp paste, oyster sauce in
  // almost everything, including dishes labeled vegetable.
  if (SE_ASIAN_LANGS.includes(sourceLanguage)) {
    return {
      id: `cuisine:se-asia:fish-sauce:${onboardingChoice}`,
      title: 'Heads up — fish sauce is everywhere here',
      body: 'Most dishes use fish sauce, shrimp paste, or oyster sauce — including ones labeled vegetable. Tag dishes with No fish sauce strict to ask the kitchen directly.',
      suggestedTag: 'no_fish_sauce',
      suggestedTagLabel: 'No fish sauce',
    };
  }

  // Spanish menus — pork hides as jamón, chorizo, lard (manteca) in beans,
  // soups, and tortilla. Critical for halal/kosher.
  if (sourceLanguage === 'es' && (onboardingChoice === 'halal' || onboardingChoice === 'kosher' || onboardingChoice === 'non_medical')) {
    return {
      id: `cuisine:spain:pork:${onboardingChoice}`,
      title: 'Heads up — pork sneaks into most dishes here',
      body: 'Lard (manteca) hides in beans, soups, and tortilla. Jamón and chorizo show up in salads and rice. Tag dishes with No pork lard strict for the strongest protection.',
      suggestedTag: 'no_pork_lard',
      suggestedTagLabel: 'No pork lard',
    };
  }

  // Japanese menus — dashi (bonito or kombu stock, sometimes pork) is in
  // every soup base, many sauces, and most "vegetable" sides.
  if (sourceLanguage === 'ja' && (onboardingChoice === 'kosher' || onboardingChoice === 'non_medical')) {
    return {
      id: `cuisine:japan:dashi:${onboardingChoice}`,
      title: 'Heads up — dashi is in almost everything',
      body: 'Dashi (bonito or pork stock) is the base for soups, noodles, and most "vegetable" sides. Tag dishes with No fish sauce strict to ask, and confirm directly with the kitchen for vegetarian or kosher meals.',
      suggestedTag: 'no_fish_sauce',
      suggestedTagLabel: 'No fish sauce',
    };
  }

  // Italian menus — pancetta, prosciutto, anchovy hide in pasta sauces and
  // even dressings. Critical for halal/kosher and strict vegetarian.
  if (sourceLanguage === 'it' && (onboardingChoice === 'halal' || onboardingChoice === 'kosher' || onboardingChoice === 'non_medical')) {
    return {
      id: `cuisine:italy:pork-anchovy:${onboardingChoice}`,
      title: 'Heads up — pork and anchovy hide in sauces',
      body: 'Pancetta, prosciutto, and anchovy show up in pasta sauces, dressings, and even some pizzas. Tag dishes with No pork lard strict and ask before ordering.',
      suggestedTag: 'no_pork_lard',
      suggestedTagLabel: 'No pork lard',
    };
  }

  return null;
}

const DISMISS_KEY_PREFIX = 'mtm:cuisineHint:dismissed:';

export function isCuisineHintDismissed(id: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(DISMISS_KEY_PREFIX + id) === '1';
}

export function dismissCuisineHint(id: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(DISMISS_KEY_PREFIX + id, '1');
}
